import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, Phone, ShieldCheck, Zap, Layers } from "lucide-react";
import { EligibilityForm } from "./EligibilityForm";
import { COMPANY } from "@/data/content";
import { scrollToId } from "@/lib/api";

const EASE = [0.22, 1, 0.36, 1];
const LINES = [
  { t: "Business", c: "" },
  { t: "funding is", c: "" },
  { t: "what we do.", c: "text-brand-gradient pr-2" },
];

const MaskLine = ({ children, i, className }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%", rotate: 3 }}
      animate={{ y: "0%", rotate: 0 }}
      transition={{ duration: 1.1, delay: 0.15 + i * 0.13, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Orbit = () => (
  <svg viewBox="0 0 1200 500" className="pointer-events-none absolute -left-[10%] top-[6%] w-[120%] opacity-90" aria-hidden>
    <defs>
      <linearGradient id="orb" x1="0" x2="1">
        <stop offset="0%" stopColor="#0b5ed7" stopOpacity="0" />
        <stop offset="35%" stopColor="#1fa2ef" />
        <stop offset="75%" stopColor="#2f9e3a" />
        <stop offset="100%" stopColor="#8bd43f" />
      </linearGradient>
    </defs>
    <motion.path
      d="M60 360 C 220 470, 900 420, 1130 170 C 1210 80, 1000 40, 760 70"
      fill="none"
      stroke="url(#orb)"
      strokeWidth="10"
      strokeLinecap="round"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 2, delay: 0.6, ease: EASE }}
    />
  </svg>
);

const TiltCard = ({ children }) => {
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const sx = useSpring(rx, { stiffness: 120, damping: 14 });
  const sy = useSpring(ry, { stiffness: 120, damping: 14 });
  const move = (e) => {
    if (window.innerWidth < 1024) return;
    const r = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 7);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 7);
  };
  const leave = () => { rx.set(0); ry.set(0); };
  return (
    <div style={{ perspective: 1400 }}>
      <motion.div
        onMouseMove={move}
        onMouseLeave={leave}
        style={{ rotateX: sx, rotateY: sy, transformStyle: "preserve-3d" }}
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.5, ease: EASE }}
        className="relative rounded-[28px] border border-white bg-white/90 p-6 shadow-[0_40px_100px_-30px_rgba(6,26,61,0.35)] backdrop-blur-xl sm:p-8"
      >
        <div className="pointer-events-none absolute -inset-px rounded-[28px] bg-gradient-to-br from-[var(--sky)]/30 via-transparent to-[var(--lime)]/30 opacity-70 [mask:linear-gradient(#000,#000)_content-box,linear-gradient(#000,#000)] [mask-composite:exclude] p-px" />
        {children}
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "14%"]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative overflow-hidden pb-20 pt-28 lg:pb-28 lg:pt-36">
      <div className="grid-lines absolute inset-0 [mask-image:radial-gradient(ellipse_at_30%_20%,#000_20%,transparent_70%)]" />
      <div className="absolute -right-40 top-20 h-[520px] w-[520px] rounded-full bg-[var(--sky)]/20 blur-[120px]" />
      <div className="absolute -left-40 bottom-0 h-[420px] w-[420px] rounded-full bg-[var(--lime)]/20 blur-[120px]" />

      <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:gap-10">
        <motion.div style={{ y: textY }} className="relative lg:col-span-7">
          <Orbit />
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="relative flex items-center gap-3 font-mono text-xs uppercase tracking-[0.22em] text-[var(--blue)]">
            <span className="h-2 w-2 animate-pulse rounded-full bg-[var(--green)]" /> Approved & funded fast
          </motion.div>
          <h1 data-testid="hero-title" className="relative mt-6 font-display text-[clamp(2.6rem,7.2vw,6rem)] font-black italic leading-[0.95] tracking-[-0.03em] text-[var(--ink)]">
            {LINES.map((l, i) => (
              <MaskLine key={l.t} i={i} className={l.c}>{l.t}</MaskLine>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.8, ease: EASE }} className="relative mt-8 max-w-xl text-base leading-relaxed text-[var(--ink)]/75 md:text-lg">
            Merchant cash advances, HELOCs, equipment financing and more. One short application, real specialists, and capital matched to how your business actually runs.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8, ease: EASE }} className="relative mt-9 flex flex-wrap items-center gap-4">
            <button onClick={() => scrollToId("apply")} data-testid="hero-apply-btn" className="group flex items-center gap-2 rounded-full bg-[var(--ink)] px-7 py-4 font-semibold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[var(--blue)] lg:hidden">
              Check eligibility <ArrowDownRight className="h-4 w-4 transition-transform group-hover:rotate-[-45deg]" />
            </button>
            <a href={COMPANY.phoneHref} data-testid="hero-call-btn" className="group flex items-center gap-3 rounded-full border border-[var(--ink)]/15 bg-white/60 px-6 py-4 font-semibold backdrop-blur transition-[border-color,background-color] hover:border-[var(--green)] hover:bg-white">
              <Phone className="h-4 w-4 text-[var(--green)]" /> <span className="font-mono text-sm">{COMPANY.phone}</span>
            </a>
          </motion.div>
          <motion.ul initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 0.8 }} className="relative mt-10 grid max-w-xl grid-cols-1 gap-3 text-sm sm:grid-cols-3">
            {[[ShieldCheck, "No hard credit pull"], [Zap, "Fast decisions"], [Layers, "7 funding products"]].map(([I, t]) => (
              <li key={t} className="flex items-center gap-2 font-medium text-[var(--ink)]/80"><I className="h-4 w-4 text-[var(--green)]" />{t}</li>
            ))}
          </motion.ul>

          <motion.div initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }} transition={{ duration: 1.3, delay: 0.7, ease: EASE }} className="relative mt-12">
            <div className="relative h-[240px] overflow-hidden rounded-[24px] sm:h-[300px] [clip-path:polygon(0_0,100%_0,100%_78%,88%_100%,0_100%)]">
            <motion.img style={{ y: imgY }} src="/img/hero.jpg" alt="Small business owner in her café" className="absolute left-0 top-[-12%] h-[135%] w-full object-cover object-[50%_30%]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,transparent_20%,rgba(6,26,61,0.55)_80%)]" />
            <div className="absolute bottom-5 left-5 rounded-2xl bg-white/85 px-4 py-3 backdrop-blur-md">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--green)]">Built for Main Street</div>
              <div className="font-display text-base font-extrabold italic">Capital that keeps doors open</div>
            </div>
            </div>
          </motion.div>
        </motion.div>

        <div id="apply" className="relative lg:col-span-5 lg:pt-6">
          <div className="lg:sticky lg:top-28">
            <TiltCard>
              <EligibilityForm />
            </TiltCard>
          </div>
        </div>
      </div>
    </section>
  );
};
